import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import NotFound from "./NotFound";
import ChartsDateInputs from "../components/ChartsDateInputs";
import ChartsPageBarTypeAQI from "../components/ChartsPageBarTypeAQI";
import ChartsPageLineTypeAQI from "../components/ChartsPageLineTypeAQI";
import useJWTInterceptors from "../authentication/useJWTInterceptors";

const StationFullDataPaginated = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const [validatePage, setValidatePage] = useState(true);
  const { theRouteUrl } = useParams();
  const stations = useSelector((state) => state.stations);
  const stationsLoading = stations.stationsLoading;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationLoading = information.informationLoading;
  const informationError = information.informationError;
  const SAPA = stations.stationsData.SAPA;
  const ESPS = stations.stationsData.ESPS;
  const OSPM = stations.stationsData.OSPM;
  const SATA = stations.stationsData.SATA;
  const [parameter, setParameter] = useState("empty");
  const [dataType, setDataType] = useState("empty");
  const [chartType, setChartType] = useState("bar");
  const modifiedSAPA = SAPA.map((obj) => ({ ...obj, device: "SAPA" }));
  const modifiedESPS = ESPS.map((obj) => ({ ...obj, device: "ESPS" }));
  const modifiedOSPM = OSPM.map((obj) => ({ ...obj, device: "OSPM" }));
  const modifiedSATA = SATA.map((obj) => ({ ...obj, device: "SATA" }));
  const stationsList = [].concat(
    modifiedSAPA,
    modifiedSATA,
    modifiedESPS,
    modifiedOSPM
  );
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [rangeType, setRangeType] = useState("empty");
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState();
  const [deviceID, setDeviceID] = useState();
  const [error, setError] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [dataError, setDataError] = useState(false);
  const [onlineError, setOnlineError] = useState(false);
  const [hourlyError, setHourlyError] = useState(false);
  const [dailyError, setDailyError] = useState(false);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [numberOfAllData, setNumberOfAllData] = useState();
  const [count, setCount] = useState();
  const [submitCount, setSubmitCount] = useState(0);
  const [currentStartDate, setCurrentStartDate] = useState();
  const [currentEndDate, setCurrentEndDate] = useState();
  const [currentDataType, setCurrentDataType] = useState();
  const tokenStr = process.env.REACT_APP_API_TOKEN;
  const baseURL = process.env.REACT_APP_BASE_URL;
  const [pageIsValid, setPageIsValid] = useState(true);

  useEffect(() => {
    if (!informationError && !informationLoading) {
      const theSpecificStation = informationData.find(
        (x) => x.slug === theRouteUrl
      );
      setPageIsValid(!!theSpecificStation);
      if (theSpecificStation) {
        setDevice(theSpecificStation.device);
        setDeviceID(theSpecificStation.device_id);
      } else {
        setValidatePage(false);
      }
    }
  }, [stationsLoading, informationLoading, theRouteUrl, informationData]);

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    let allData = [];
    let apiURL;
    let granularity;

    switch (dataType) {
      case "online":
        granularity = 0;
        break;
      case "hourly":
        granularity = 1;
        break;
      case "daily":
        granularity = 2;
        break;
      case "twelve":
        granularity = 3;
        break;
      case "twentyFour":
        granularity = 4;
        break;
      default:
        granularity = null;
    }

    if (granularity !== null) {
      apiURL = `${baseURL}history/${device}/${deviceID}/${granularity}?start_date=${selectedStartDate}&end_date=${selectedEndDate}&page=1&page_size=10000`;

      try {
        const response = await useJWTInterceptors.get(apiURL, {
          headers: { Authorization: `Token ${tokenStr}` },
        });

        const totalCount = response.data.count;
        setCount(totalCount);
        allData = [...response.data.results];
        // const totalPages = Math.ceil(totalCount / 50);

        // for (let page = 2; page <= totalPages; page++) {
        //   const pageUrl = `${baseURL}history/${device}/${deviceID}/${granularity}?start_date=${selectedStartDate}&end_date=${selectedEndDate}&page=${page}`;
        //   const pageResponse = await useJWTInterceptors.get(pageUrl, {
        //     headers: { Authorization: `Token ${tokenStr}` },
        //   });
        //   allData = [...allData, ...pageResponse.data.results];
        // }

        setCurrentPageData(allData);
      } catch (error) {
        setError(true);
      } finally {
        setLoading(false);
        setCurrentStartDate(selectedStartDate);
        setCurrentEndDate(selectedEndDate);
        setCurrentDataType(dataType);
      }
    }
  }, [
    dataType,
    selectedStartDate,
    selectedEndDate,
    baseURL,
    device,
    deviceID,
    tokenStr,
  ]);

  const chartLabels = useMemo(
    () =>
      currentPageData
        .map((item) => item.ccreated_datetime)
        .filter((value) => value !== null),
    [currentPageData, parameter]
  );

  const chartData = useMemo(() => {
    switch (parameter) {
      case "aqi":
        return currentPageData.map((item) => item.AQI);
      case "pm25":
        return currentPageData.map((item) => item.PM25AQI);
      case "pm10":
        return currentPageData.map((item) => item.PM10AQI);
      case "co":
        return currentPageData.map((item) => item.COAQI);
      case "o3":
        return currentPageData.map((item) => item.O3AQI);
      case "so2":
        return currentPageData.map((item) => item.SO2AQI);
      case "no2":
        return currentPageData.map((item) => item.NO2AQI);
      case "temperature":
        return currentPageData.map((item) => item.temperature);
      case "humidity":
        return currentPageData.map((item) => item.humidity);
      case "pressure":
        return currentPageData.map((item) => item.pressure);
      case "spl":
        return currentPageData.map((item) => item.spl);
      case "voc":
        return currentPageData.map((item) => item.voc);
      default:
        return [];
    }
  }, [currentPageData, parameter]);

  const onHandleSubmit = (e) => {
    e.preventDefault();

    if (
      dataType === "empty" ||
      chartType === "empty" ||
      parameter === "empty" ||
      !selectedStartDate ||
      !selectedEndDate
    ) {
      alert("لطفا همه گزینه ها را پر کنید.");
      return;
    }

    if (
      dataType !== currentDataType ||
      selectedStartDate !== currentStartDate ||
      selectedEndDate !== currentEndDate
    ) {
      setLoading(true);
      setError(false);
      setCurrentPageData([]);
    }

    setSubmitCount((prevCount) => prevCount + 1);
    fetchChartData();
  };

  if (!pageIsValid) {
    return <NotFound />;
  }

  if (!isLoggedIn) {
    return <NoAccessPage />;
  }

  return (
    <div className="container-fluid p-2 text-medium">
      <div className="row">
        <ChartsDateInputs
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          setSelectedStartDate={setSelectedStartDate}
          setSelectedEndDate={setSelectedEndDate}
          setChartType={setChartType}
          chartType={chartType}
          setDataType={setDataType}
          setParameter={setParameter}
          parameter={parameter}
          device={device}
          dataType={dataType}
          onHandleSubmit={onHandleSubmit}
          rangeType={rangeType}
          setRangeType={setRangeType}
        />
        {loading ? (
          <div className="col-lg-8 col-xl-9 d-flex align-items-center justify-content-center">
            <span className="spinner spinner-border spinner-border-lg"></span>
          </div>
        ) : error ? (
          <div className="col-lg-8 col-xl-9 d-flex align-items-center justify-content-center">
            <p>در بارگذاری داده ها خطایی رخ داده است!</p>
          </div>
        ) : chartLabels.length > 0 && chartData.length > 0 ? (
          <div className="text-medium col-lg-8 col-xl-9">
            {["aqi", "co", "o3", "so2", "no2", "pm25", "pm10"].includes(
              parameter
            ) ? (
              chartType === "bar" ? (
                <ChartsPageBarTypeAQI
                  parameter={parameter}
                  labels={chartLabels}
                  data={chartData}
                  dataType={dataType}
                  rangeType={rangeType}
                />
              ) : chartType === "scatter" ? (
                <ChartsPageLineTypeAQI
                  parameter={parameter}
                  labels={chartLabels}
                  data={chartData}
                  dataType={dataType}
                  rangeType={rangeType}
                />
              ) : null
            ) : null}
          </div>
        ) : count === 0 && currentPageData.length === 0 ? (
          <div className="col-lg-8 col-xl-9 d-flex align-items-center justify-content-center">
            <p>داده‌ای موجود نیست!</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StationFullDataPaginated;
